import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRightLeft, Plus, Search, Eye, Truck, CheckCircle2, XCircle, 
  Package, ArrowRight, Clock, Filter, RefreshCw 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDateOnly } from '@/utils/dateFormat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { stockTransfersAPI, warehousesAPI, storesAPI } from '@/services/api';

const statusConfig = {
  new: { label: 'New', color: 'bg-purple-100 text-purple-700', icon: Clock },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700', icon: Package },
  in_transit: { label: 'In Transit', color: 'bg-blue-100 text-blue-700', icon: Truck },
  received: { label: 'Received', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  discrepancy: { label: 'Discrepancy', color: 'bg-orange-100 text-orange-700', icon: XCircle },
  resolved: { label: 'Resolved', color: 'bg-cyan-100 text-cyan-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
  // Legacy statuses
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Clock },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock }
};

const StockTransfersPage = () => {
  const [transfers, setTransfers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  
  // Action dialogs
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [transfersRes, warehousesRes, storesRes] = await Promise.all([
        stockTransfersAPI.list(params),
        warehousesAPI.list({ is_active: true }),
        storesAPI.list({ is_active: true })
      ]);
      
      setTransfers(transfersRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setStores(storesRes.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch transfers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewTransfer = async (transfer) => {
    try {
      const response = await stockTransfersAPI.get(transfer.id);
      setSelectedTransfer(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load transfer details', variant: 'destructive' });
    }
  };

  const handleDispatch = async () => {
    setActionLoading(true);
    try {
      await stockTransfersAPI.dispatch(selectedTransfer.id);
      toast({ title: 'Success', description: 'Transfer dispatched successfully' });
      setDispatchDialogOpen(false);
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to dispatch', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceive = async () => {
    setActionLoading(true);
    try {
      await stockTransfersAPI.receive(selectedTransfer.id);
      toast({ title: 'Success', description: 'Transfer received successfully' });
      setReceiveDialogOpen(false);
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to receive', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast({ title: 'Error', description: 'Please provide cancellation reason', variant: 'destructive' });
      return;
    }
    setActionLoading(true);
    try {
      await stockTransfersAPI.cancel(selectedTransfer.id, cancelReason);
      toast({ title: 'Success', description: 'Transfer cancelled' });
      setCancelDialogOpen(false);
      setViewDialogOpen(false);
      setCancelReason('');
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to cancel', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const filteredTransfers = transfers.filter(t => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return t.transfer_number?.toLowerCase().includes(searchLower) ||
           t.from_location_name?.toLowerCase().includes(searchLower) ||
           t.to_location_name?.toLowerCase().includes(searchLower);
  });

  // Summary counts
  const draftCount = transfers.filter(t => t.status === 'draft').length;
  const inTransitCount = transfers.filter(t => t.status === 'in_transit').length;
  const receivedCount = transfers.filter(t => t.status === 'received').length;

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="stock-transfers-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Stock Transfers</h1>
          <p className="text-gray-500">Transfer stock between warehouses and stores</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="border-gray-300" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button 
            onClick={() => navigate('/hq/inventory/transfers/new')} 
            className="bg-teal-500 hover:bg-teal-600 text-white"
            data-testid="new-transfer-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Transfer
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Transfers</p>
                <p className="text-2xl font-bold text-gray-800">{transfers.length}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <ArrowRightLeft className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Draft</p>
                <p className="text-2xl font-bold text-yellow-600">{draftCount}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">In Transit</p>
                <p className="text-2xl font-bold text-blue-600">{inTransitCount}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Truck className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Received</p>
                <p className="text-2xl font-bold text-emerald-600">{receivedCount}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search transfers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white border-gray-300" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transfers Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : filteredTransfers.length === 0 ? (
            <div className="text-center py-12">
              <ArrowRightLeft className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No transfers found</p>
              <Button 
                onClick={() => navigate('/hq/inventory/transfers/new')} 
                variant="outline" 
                className="mt-4 border-gray-300"
              >
                Create your first transfer
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-gray-200">
                    <TableHead className="text-gray-700 font-semibold">Transfer #</TableHead>
                    <TableHead className="text-gray-700 font-semibold">From</TableHead>
                    <TableHead className="text-gray-700 font-semibold">To</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-center">Items</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-right">Value</TableHead>
                    <TableHead className="text-gray-700 font-semibold">Date</TableHead>
                    <TableHead className="text-gray-700 font-semibold">Status</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransfers.map((transfer) => {
                    const config = statusConfig[transfer.status] || statusConfig.draft;
                    const StatusIcon = config.icon;
                    return (
                      <TableRow key={transfer.id} className="border-gray-200 hover:bg-gray-50" data-testid={`transfer-row-${transfer.id}`}>
                        <TableCell>
                          <p className="font-medium text-teal-600">{transfer.transfer_number}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="text-gray-800">{transfer.from_location_name}</p>
                              <p className="text-xs text-gray-500 capitalize">{transfer.from_location_type}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <ArrowRight className="w-4 h-4 text-teal-500" />
                            <div>
                              <p className="text-gray-800">{transfer.to_location_name}</p>
                              <p className="text-xs text-gray-500 capitalize">{transfer.to_location_type}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{transfer.total_items} items</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium text-gray-800">
                          ₹{transfer.total_value?.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {formatDateOnly(transfer.transfer_date)}
                        </TableCell>
                        <TableCell>
                          <Badge className={config.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {config.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewTransfer(transfer)}
                            className="text-gray-600 hover:text-teal-600"
                            data-testid={`view-transfer-${transfer.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Transfer Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-teal-500" />
              Transfer {selectedTransfer?.transfer_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTransfer && (
            <div className="space-y-6">
              {/* Transfer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">From</p>
                  <p className="font-semibold text-gray-800">{selectedTransfer.from_location_name}</p>
                  <p className="text-sm text-gray-500 capitalize">{selectedTransfer.from_location_type} - {selectedTransfer.from_location_code}</p>
                </div>
                <div className="bg-teal-50 p-4 rounded-lg">
                  <p className="text-sm text-teal-600 mb-1">To</p>
                  <p className="font-semibold text-gray-800">{selectedTransfer.to_location_name}</p>
                  <p className="text-sm text-gray-500 capitalize">{selectedTransfer.to_location_type} - {selectedTransfer.to_location_code}</p>
                </div>
              </div>

              {/* Status & Date */}
              <div className="flex items-center justify-between">
                <div>
                  <Badge className={statusConfig[selectedTransfer.status]?.color || 'bg-gray-100'}>
                    {statusConfig[selectedTransfer.status]?.label || selectedTransfer.status}
                  </Badge>
                </div>
                <div className="text-sm text-gray-500">
                  Transfer Date: {formatDateOnly(selectedTransfer.transfer_date)}
                </div>
              </div>

              {/* Items Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-gray-700">Product</TableHead>
                      <TableHead className="text-gray-700">Batch</TableHead>
                      <TableHead className="text-gray-700 text-center">Qty</TableHead>
                      <TableHead className="text-gray-700 text-center">Received</TableHead>
                      <TableHead className="text-gray-700 text-right">Cost</TableHead>
                      <TableHead className="text-gray-700 text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTransfer.items?.map((item, idx) => (
                      <TableRow key={idx} className="border-gray-200">
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-teal-600">{item.product_sku}</p>
                        </TableCell>
                        <TableCell className="text-gray-600">{item.batch_number || '-'}</TableCell>
                        <TableCell className="text-center font-medium">{item.quantity} {item.uom}</TableCell>
                        <TableCell className="text-center">
                          {selectedTransfer.status === 'received' ? (
                            <span className="text-emerald-600 font-medium">{item.received_qty || item.quantity}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-right text-gray-600">₹{item.cost_price?.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium text-gray-800">₹{item.line_value?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              <div className="flex justify-end">
                <div className="bg-gray-50 p-4 rounded-lg text-right">
                  <p className="text-sm text-gray-500">Total Items: {selectedTransfer.total_items}</p>
                  <p className="text-sm text-gray-500">Total Qty: {selectedTransfer.total_quantity}</p>
                  <p className="text-lg font-bold text-gray-800">Total Value: ₹{selectedTransfer.total_value?.toLocaleString()}</p>
                </div>
              </div>

              {/* Notes */}
              {selectedTransfer.notes && (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Notes:</strong> {selectedTransfer.notes}
                  </p>
                </div>
              )}

              {/* Cancellation reason */}
              {selectedTransfer.cancellation_reason && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>Cancellation Reason:</strong> {selectedTransfer.cancellation_reason}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            {selectedTransfer?.status === 'draft' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setCancelReason('');
                    setCancelDialogOpen(true);
                  }}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Cancel Transfer
                </Button>
                <Button 
                  onClick={() => setDispatchDialogOpen(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="dispatch-btn"
                >
                  <Truck className="w-4 h-4 mr-2" /> Dispatch
                </Button>
              </>
            )}
            {selectedTransfer?.status === 'in_transit' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setCancelReason('');
                    setCancelDialogOpen(true);
                  }}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Cancel Transfer
                </Button>
                <Button 
                  onClick={() => setReceiveDialogOpen(true)}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="receive-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Receive
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="border-gray-300">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Confirmation */}
      <AlertDialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Dispatch Transfer?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deduct stock from the source location and mark the transfer as "In Transit".
              Make sure all items are ready for dispatch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDispatch} 
              disabled={actionLoading}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              {actionLoading ? 'Dispatching...' : 'Confirm Dispatch'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Receive Confirmation */}
      <AlertDialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Receive Transfer?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will add stock to the destination location and complete the transfer.
              Verify all items have been received correctly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReceive} 
              disabled={actionLoading}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {actionLoading ? 'Receiving...' : 'Confirm Receipt'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Cancel Transfer?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              {selectedTransfer?.status === 'in_transit' 
                ? 'Stock will be restored to the source location.' 
                : 'This transfer will be marked as cancelled.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter cancellation reason..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="bg-gray-50 border-gray-300"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Back</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancel} 
              disabled={actionLoading || !cancelReason.trim()}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {actionLoading ? 'Cancelling...' : 'Cancel Transfer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StockTransfersPage;
